import { useQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { createAgentInteraction, getAgentClients } from "../api/endpoints/agents"
import { AgentClientDrawer } from "../components/agent_client_drawer"
import { EmptyState, PageSection, StatusPill } from "../components/ui"
import { useAuth } from "../stores/auth"
import { formatCurrency, formatDateTime } from "../utils/formatting"
import { flushInteractionQueue, loadAgentCache, saveAgentCache } from "../utils/offline_queue"

export function AgentClientsPage() {
  const { user } = useAuth()
  const agentId = user?.userId ?? ""
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null)
  const [search, setSearch] = useState("")
  const [overdueCategory, setOverdueCategory] = useState("")
  const [pipelineStatus, setPipelineStatus] = useState("")
  const [sortBy, setSortBy] = useState("days_overdue")
  const [sortOrder, setSortOrder] = useState("desc")
  const [page, setPage] = useState(1)

  const clientsQuery = useQuery({
    queryKey: ["agent-clients", agentId, search, overdueCategory, pipelineStatus, sortBy, sortOrder, page],
    queryFn: () => {
      const params: Record<string, string> = {
        page: String(page),
        per_page: "25",
        sort_by: sortBy,
        sort_order: sortOrder,
      }
      if (search) params.search = search
      if (overdueCategory) params.overdue_category = overdueCategory
      if (pipelineStatus) params.pipeline_status = pipelineStatus
      return getAgentClients(agentId, params)
    },
    enabled: Boolean(agentId),
  })

  useEffect(() => {
    if (clientsQuery.data?.data && agentId) {
      saveAgentCache(agentId, clientsQuery.data.data)
    }
  }, [clientsQuery.data, agentId])

  useEffect(() => {
    async function syncQueuedInteractions() {
      await flushInteractionQueue((queuedAgentId, payload) =>
        createAgentInteraction(queuedAgentId, payload),
      )
    }

    window.addEventListener("online", syncQueuedInteractions)
    return () => window.removeEventListener("online", syncQueuedInteractions)
  }, [])

  const cached = loadAgentCache(agentId)
  const rows = clientsQuery.data?.data?.rows ?? cached.rows
  const meta = clientsQuery.data?.meta

  return (
    <div className="flex flex-col gap-5">
      <PageSection title="My clients">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[2fr,1fr,1fr,1fr,auto]">
          <input
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
            placeholder="Search client, phone, or loan ID"
            value={search}
          />
          <select
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            onChange={(event) => {
              setOverdueCategory(event.target.value)
              setPage(1)
            }}
            value={overdueCategory}
          >
            <option value="">All overdue categories</option>
            <option value="S1">S-1</option>
            <option value="CATEGORY_A">Category A</option>
            <option value="CATEGORY_B">Category B</option>
            <option value="CATEGORY_C">Category C</option>
          </select>
          <select
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            onChange={(event) => {
              setPipelineStatus(event.target.value)
              setPage(1)
            }}
            value={pipelineStatus}
          >
            <option value="">All pipeline statuses</option>
            <option value="NEW">New</option>
            <option value="OLD">Old</option>
            <option value="BROKEN_PROMISE">Broken Promise</option>
            <option value="PROMISE_TO_PAY">Promise to Pay</option>
            <option value="DONE">Done</option>
          </select>
          <select
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            onChange={(event) => setSortBy(event.target.value)}
            value={sortBy}
          >
            <option value="days_overdue">Sort by days overdue</option>
            <option value="outstanding_balance">Sort by outstanding amount</option>
            <option value="last_contact_at">Sort by last interaction</option>
            <option value="ptp_date">Sort by PTP date</option>
            <option value="client_name">Sort by client name</option>
          </select>
          <button
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700"
            onClick={() => setSortOrder((value) => (value === "asc" ? "desc" : "asc"))}
            type="button"
          >
            {sortOrder === "asc" ? "Ascending" : "Descending"}
          </button>
        </div>

        {rows.length === 0 ? (
          <EmptyState
            description="Assigned clients will appear here once the portfolio is seeded or reassigned."
            title="No assigned clients"
          />
        ) : null}

        <table className="w-full text-left text-xs">
          <thead>
            <tr className="bg-slate-50 text-slate-500 font-bold uppercase border-b border-slate-100">
              <th className="px-4 py-3">Client</th>
              <th className="px-4 py-3">Outstanding</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Pipeline</th>
              <th className="px-4 py-3">Last contact</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row: any) => (
              <tr
                className="hover:bg-slate-50 transition-colors cursor-pointer"
                key={row.loanId}
                onClick={() => setSelectedClientId(row.clientId)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    setSelectedClientId(row.clientId)
                  }
                }}
                tabIndex={0}
              >
                <td className="px-4 py-3">
                  <strong>{row.clientName}</strong>
                  <div className="text-xs text-slate-500">{row.loanId}</div>
                </td>
                <td className="px-4 py-3">{formatCurrency(row.outstandingBalance, row.currency)}</td>
                <td className="px-4 py-3">{row.overdueCategory ?? "-"}</td>
                <td className="px-4 py-3">
                  <StatusPill>{row.pipelineStatus}</StatusPill>
                </td>
                <td className="px-4 py-3">{formatDateTime(row.lastContactAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 text-sm text-slate-600">
            <span>
              Page {meta.page} of {meta.totalPages}
            </span>
            <div className="flex gap-2">
              <button
                className="rounded-md border border-slate-200 bg-white px-3 py-1.5"
                disabled={(meta.page ?? 1) <= 1}
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                type="button"
              >
                Previous
              </button>
              <button
                className="rounded-md border border-slate-200 bg-white px-3 py-1.5"
                disabled={(meta.page ?? 1) >= (meta.totalPages ?? 1)}
                onClick={() => setPage((value) => value + 1)}
                type="button"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </PageSection>

      <AgentClientDrawer
        agentId={agentId}
        clientId={selectedClientId}
        onClose={() => setSelectedClientId(null)}
      />
    </div>
  )
}
